import { Stack, Box, Divider } from '@mui/material';
import pluralize from 'pluralize';
import { AdvancedFilter } from '../../common/filters/advanced-filter/AdvancedFilter';
import { SearchInput } from '../../common/SearchInput';
import { DataTable } from '../../common/table/DataTable';
import { ACTION_BUTTONS_KEY, DataTableId } from '../../common/table/utils';
import { globalSearchSx } from '../canvas-pages/canvasPanelConsts';
import {
  ConversationStatus,
  DataDisplayRow,
  AiAssistantConversation,
  AiAssistantFilters,
} from './types';
import { columns, getConvertedStatus, getPageLink, getStatusIconByType } from './utils';
import { WrapperWithHeader } from './WrapperWithHeader';
import { IconButton, Tooltip, Typography, colors } from '@sweep-io/sweep-design';
import { useState } from 'react';
import { filterItemsBySearch } from '../../../lib/filterItemsBySearch';
import { humanizeDate, humanizeDateVariants } from '../../helpers/humanizeDate';
import { Check } from '@sweep-io/sweep-design/dist/icons';
import { useAiAssistant } from './useAiAssistant';
import { DataTableVariant } from '../../common/table/TableTypes';

const filterByDate = (
  items: AiAssistantConversation[],
  startTime?: string,
  endTime?: string,
): AiAssistantConversation[] => {
  if (!startTime && !endTime) return items;

  const start = startTime ? new Date(startTime) : new Date(0); // Use epoch time as default start
  const end = endTime ? new Date(endTime) : new Date(); // Use current time as default end

  return items?.filter((item) => {
    const itemDate = new Date(item.updatedAt);
    return itemDate >= start && itemDate <= end;
  });
};

interface AiAssistantConversationsProps {
  items: AiAssistantConversation[];
  setConversationItem: (item: AiAssistantConversation) => void;
  dateFilters: AiAssistantFilters;
  crmOrgId: string;
  selectedConversation?: AiAssistantConversation;
  isCollapsed?: boolean;
}

export const AiAssistantConversations = ({
  items,
  selectedConversation,
  setConversationItem,
  dateFilters,
  crmOrgId,
  isCollapsed,
}: AiAssistantConversationsProps) => {
  const { markAsResolved } = useAiAssistant();
  const [searchUsers, setSearchUsers] = useState<string>('');
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);

  const filteredUsers = filterItemsBySearch<AiAssistantConversation>(
    items,
    searchUsers,
    'username',
  );

  const filteredByStatus =
    selectedStatuses.length > 0
      ? filteredUsers.filter((el) => selectedStatuses.includes(getConvertedStatus(el.status)))
      : filteredUsers;

  const filteredByDate = filterByDate(filteredByStatus, dateFilters.startTime, dateFilters.endTime);

  const renderActions = (dataRow: AiAssistantConversation) => {
    const _status = getConvertedStatus(dataRow.status);
    return (
      <>
        {(_status === ConversationStatus.Ask_Admin || _status === ConversationStatus.Open) && (
          <Tooltip title="Mark as resolved">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                markAsResolved(dataRow, crmOrgId);
              }}
              variant="system"
              size="small"
            >
              <Check />
            </IconButton>
          </Tooltip>
        )}
      </>
    );
  };

  const sortedWithIcon =
    filteredByDate?.map((data) => {
      return {
        id: data.id,
        user: data.username,
        page: getPageLink(data, isCollapsed),
        status: (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {getStatusIconByType(data.status)}
            <Typography
              variant={isCollapsed ? 'caption' : 'body'}
              color={colors.grey[800]}
              whiteSpace="nowrap"
            >
              {ConversationStatus[data.status as keyof typeof ConversationStatus]}
            </Typography>
          </Box>
        ),
        [ACTION_BUTTONS_KEY]: renderActions(data),
        date: (
          <Typography
            variant={isCollapsed ? 'caption' : 'body'}
            color={colors.grey[800]}
            whiteSpace="nowrap"
            ellipsis
          >
            {humanizeDate({
              dateOrTimestamp: data.updatedAt ?? '',
              variant: humanizeDateVariants.TWO_DIGITS_WITH_TIME,
            })}
          </Typography>
        ),
        isRowActive: data.id === selectedConversation?.id,
      };
    }) ?? [];

  const renderFilter = () => {
    return (
      <AdvancedFilter
        items={[
          {
            label: ConversationStatus.Ask_Admin,
            value: ConversationStatus.Ask_Admin,
          },
          { label: ConversationStatus.Open, value: ConversationStatus.Open },
          {
            label: ConversationStatus.Closed_By_Admin,
            value: ConversationStatus.Closed_By_Admin,
          },
          {
            label: ConversationStatus.Closed_By_Rep,
            value: ConversationStatus.Closed_By_Rep,
          },
        ]}
        selectedItems={selectedStatuses}
        onSelectedItemsChange={setSelectedStatuses}
        texts={{ title: 'Status' }}
        titleVariant={'caption'}
        titleColor={colors.grey[700]}
      />
    );
  };

  return (
    <WrapperWithHeader
      title="Conversations"
      sx={{ height: '100%', overflow: 'hidden', gap: isCollapsed ? 1 : 2 }}
    >
      <Stack height="100%" overflow="hidden">
        <Stack
          alignItems="center"
          direction="row"
          sx={{
            flex: 1,
            justifyContent: 'space-between',
            mb: isCollapsed ? 1 : 2,
            ...globalSearchSx(true),
          }}
        >
          {!isCollapsed && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              <Typography variant="caption" color={colors.grey[700]}>
                {`${filteredByDate?.length} ${pluralize('Interaction', filteredByDate?.length)}`}
              </Typography>
              <Divider orientation="vertical" flexItem />
              {renderFilter()}
            </Box>
          )}

          <Box sx={{ width: isCollapsed ? '100%' : '300px' }}>
            <SearchInput
              size="small"
              withFixedMagnifyingGlassIcon
              TextFieldProps={{
                value: searchUsers,
                sx: {
                  width: isCollapsed ? '100%' : '300px',
                },
                onChange: (e) => {
                  setSearchUsers(e.target.value);
                },
                placeholder: 'Search for user',
              }}
              onClearButtonClick={() => setSearchUsers('')}
            />
          </Box>
        </Stack>

        {isCollapsed && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              pb: 1,
            }}
          >
            <Typography variant="caption" color={colors.grey[700]}>
              {`${filteredByDate?.length} ${pluralize('Interaction', filteredByDate?.length)}`}
            </Typography>
            {renderFilter()}
          </Box>
        )}

        <DataTable
          stickyHeader
          tableId={DataTableId.AiAssistantPage}
          hideChevronButton
          onRowClick={(
            e: React.MouseEvent<HTMLTableCellElement, MouseEvent>,
            row: DataDisplayRow,
          ) => {
            const item = filteredByDate?.find((el) => el.id === row.id);
            item && setConversationItem(item);
          }}
          columns={columns}
          rows={sortedWithIcon}
          tableEmptyStateJsx={
            <Box sx={{ mt: '150px', textAlign: 'center', height: '100%' }}>
              <Typography variant="body" color={colors.grey[800]}>
                Your team conversations will appear here
              </Typography>
            </Box>
          }
          variant={isCollapsed ? DataTableVariant.small : undefined}
        />
      </Stack>
    </WrapperWithHeader>
  );
};
